/* eslint-disable no-nested-ternary */
import CircularProgress from '@mui/material/CircularProgress'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Collapse from '@mui/material/Collapse'
import React, { useState } from 'react'
import List from '@mui/material/List'
import Box from '@mui/material/Box'

import { CategoriesType, CategoryType } from 'shared/store/useCategories/types'

interface Props {
   categories: CategoriesType
   onSelect?: (uuid: string) => void
   expandedIds?: Set<string>
   selectedId: string
}

function CategoryNode({
   category,
   onSelect,
   selectedId,
   level = 0,
   expandedIds,
}: {
   category: CategoryType
   onSelect?: (uuid: string) => void
   level?: number
   selectedId: string
   expandedIds?: Set<string>
}) {
   const [manualOpen, setManualOpen] = useState(false)

   const hasChildren = !!category.children?.length

   const open = (expandedIds?.has(category.uuid) ?? false) || manualOpen

   const handleClick = () => {
      if (hasChildren) {
         setManualOpen((prev) => !prev)
      }
   }

   const isSelected = selectedId === category.uuid

   return (
      <Box>
         <ListItemButton
            onClick={() => {
               onSelect?.(category.uuid)
            }}
            sx={(theme) => ({
               pt: '2px',
               pb: '2px',
               pl: 1 + level * 2,
               transition: 'background-color 0.2s ease',
               '&:hover': {
                  cursor: 'pointer',
                  color: 'white',
                  backgroundColor: theme.palette.primary.light,
               },
               color: isSelected ? theme.palette.primary.main : null,
               borderRight: isSelected ? '3px solid ' : null,
               borderRightColor: theme.palette.primary.main,
            })}
         >
            <ListItemText
               primary={category.name}
               slotProps={{
                  primary: {
                     fontWeight: isSelected ? 'bold' : 'normal',
                  },
               }}
            />
            {hasChildren ? (
               open ? (
                  <ExpandLess
                     onClick={(e) => {
                        e.stopPropagation()
                        handleClick()
                     }}
                  />
               ) : (
                  <ExpandMore
                     onClick={(e) => {
                        e.stopPropagation()
                        handleClick()
                     }}
                  />
               )
            ) : null}
         </ListItemButton>
         {hasChildren && (
            <Collapse in={open} timeout="auto" unmountOnExit>
               <List component="div" disablePadding>
                  {category.children!.map((child) => (
                     <CategoryNode
                        key={child.uuid}
                        category={child}
                        onSelect={onSelect}
                        selectedId={selectedId}
                        level={level + 1}
                        expandedIds={expandedIds}
                     />
                  ))}
               </List>
            </Collapse>
         )}
      </Box>
   )
}

export function CategoriesTree({ categories, onSelect, selectedId, expandedIds }: Props) {
   return (
      <Box sx={{ maxHeight: '450px', overflow: !categories.categories ? 'hidden' : 'auto' }}>
         {!categories.categories ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
               <CircularProgress color="primary" />
            </Box>
         ) : (
            <List>
               {categories.categories?.map((cat) => (
                  <CategoryNode
                     key={cat.uuid}
                     category={cat}
                     onSelect={onSelect}
                     selectedId={selectedId}
                     level={0}
                     expandedIds={expandedIds}
                  />
               ))}
            </List>
         )}
      </Box>
   )
}
